import React, { useCallback, useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Tooltip from '@material-ui/core/Tooltip';
import classnames from 'classnames';
import { format, parse } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import EditIcon from 'assets/images/edit-icon.svg';
import DuplicateI from 'assets/images/duplicate.svg';
import HistoryI from 'assets/images/history-icon.svg';
import userPhoto from 'assets/icons/user-photo-placeholder.svg';
import { getProjectName, hasPermissionsFor } from 'helpers/_helpers';
import { agoDate, clockTime } from 'helpers/_date-helpers';
import QualityControlActions from '../QualityControlActions/QualityControlActions';
import { useWorklogStyles } from '../../hooks';
import PrintIcon from 'assets/images/print-icon.svg';
import { useParams } from 'react-router-dom';
import { fetchProjects } from 'store/projectTypes/projectTypesOperations';
import { fetchAllContractorsData } from 'store/contractors/contractorsPageOperations';
import projectTypesActions from 'store/projectTypes/projectTypesActions';
import { SheetsContacts } from './components/SheetsContacts/SheetsContacts';
import OptionalModal from 'components/OptionalModal/OptionalModal';
import { globalBEDateFormat, GLOBAL_DATE_FORMAT, getTimeFormat } from 'common/dateFormatConfig';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { verifyWorklog, removeDwlUser } from 'store/currentWorklog/operations';
import VerifyModal from './components/GeneralInfoForm/VerifyModal/VerifyModal';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Paper } from '../../../../../node_modules/@material-ui/core/index';
import RedirectVerifyModal from './components/GeneralInfoForm/RedirectVerifyModal/RedirectVerifyModal';
import styles from './styles';
import { useArrayHistoryChanges } from 'components/WorklogDetails/hooks/useArrayHistoryChanges';
import BusinessIcon from 'assets/images/ion_business.svg';
import { PAGE_CONTEXTS } from 'components/WorklogDetails/constants';
import LocationPin from 'components/LocationPin/LocationPin';

const options = {
  title: 'Do you want to include image(s) to the print?',
  text: '',
  trueOption: 'Yes',
  falseOption: 'No',
};

const GeneralInfoBlock = ({
  hasAccess,
  sheet,
  user,
  close,
  openSnackbar,
  openAdditionalDrawer,
  setDuplicateModal,
  onHistoryDrawerOpen,
  historyState,
  classes,
  pageContext,
}) => {
  const worklogClasses = useWorklogStyles();
  const dispatch = useDispatch();
  const { type } = useParams();
  const { dayDateSettingsFormat, dateFormatWithTime } = useDateSettingsFormat();
  const originalWl = useSelector((store) => store.currentWorklog?.worklogSheet?.sheet)
  const { dateFormat, timeFormat } = useSelector((store) => (store.personalProfile.organization?.settings || {}))
  const locations = useSelector((state) => state.personalProfile.activeLocations);

  const [printModal, setPrintModal] = useState(false);
  const [verifyModal, setVerifyModal] = useState({ open: false });
  const [redirectModal, setRedirectModal] = useState({ open: false });
  const [detailsShown, setDetailsShown] = useState(false);

  const openPrintPage = (query) => {
    const path = type == 'worklog-foreman' ? '/isCrew' : '';
    const url = `/worklog/print/${sheet._id}${path}${query === 'true' ? '?printImg=true' : ''}`;
    window.open(url, '_blank', 'noreferrer');
  };

  useEffect(() => {
    dispatch(fetchProjects({ page: NaN }, { id: sheet._id }));
    dispatch(fetchAllContractorsData({ id: sheet._id }));
    return () => dispatch(projectTypesActions.resetProjects());
  }, []);


  const lastSubmission = useMemo(() => {
    if (historyState) {
      return originalWl?.submissions?.find((sub) => sub?._id === historyState?.submissionId);
    }
    const submissions = [...(sheet?.submissions || [])];
    return submissions.sort((a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime())?.[0];
  }, [sheet, originalWl, historyState])

  const isVerified = sheet.verificationStatus === 'verified'
  const isPendingVerification = sheet.verificationStatus === 'pending'

  const getSheetOwner = () => {
    return sheet.ownerUsername ? `by ${sheet.ownerUsername}` : null;
  };

  const getSheetStatus = () => {
    const { published, submittedAt, canceledAt } = sheet;

    if (!published) return 'Draft';
    if (submittedAt && (lastSubmission || !!historyState?.submissionId)) {
      return `Submitted on ${agoDate(submittedAt, null, dayDateSettingsFormat)} by ${lastSubmission?.username}`;
    }
    return canceledAt ? 'Canceled' : 'Published';
  };

  const getSheetVerificationStatus = () => {
    if (sheet.verificationStatus === 'pending') {
      return 'Pending Verification'
    }
    const lastDate = lastSubmission?.dwlVerification?.reduce((acc, user) => {
      if (user.verifiedAt && new Date(user.verifiedAt)?.getTime() > acc.getTime()) return new Date(user.verifiedAt);
      if (user.redirectedAt && new Date(user.redirectedAt)?.getTime() > acc.getTime()) return new Date(user.redirectedAt);
      return acc;
    }, new Date(0))

    if (!lastDate) return null;
    
    return `Verified ${agoDate(lastDate, null, dayDateSettingsFormat)}`
  }

  const sheetVerificationColor = (sheet.verificationStatus) &&
    /Pending Verification/i.test(getSheetVerificationStatus())
      ? '#3E82FF'
      : '#0C842B';

  const getImageSrc = (public_id) => {
    return userPhoto;
  };

  const formatGridDate = (date) =>
    format(parse(date, globalBEDateFormat, new Date()), dayDateSettingsFormat);

  const findLocation = (sheet, locationId, fieldName) => {
    const allPlaces =
      fieldName === 'sheetRallyPoints'
        ? [...(sheet?.sheetRallyPoints || []), ...(sheet.project?.rallyPoints || [])]
        : [...(sheet?.sheetLocations || []), ...(sheet.project?.locations || [])];
    return allPlaces.find((l) => l._id === locationId);
  };

  const sheetLocation = (sheet, isStandalone, locationId, fieldName) => {
    if (!sheet.project) return '';
    const location = findLocation(sheet, locationId, fieldName);
    if (isStandalone) {
      if (!location) return '';
      const locationZip = location.zipcode ? `, ${location.zipcode}` : '';
      return location.name || `${location.address}${locationZip}`;
    }
    const nameOrAddress = location.name || location.address;
    return location && nameOrAddress ? ', ' + nameOrAddress : '';
  };

  const sheetHasLocation = sheet.locations && sheet.locations.length && !!sheet.locations[0];

  const duplicatePermissions =
    hasPermissionsFor('scheduleEdit') || hasPermissionsFor('scheduleFullAccess');
  const duplicateRule =
    !sheet.canceledAt && duplicatePermissions && pageContext !== PAGE_CONTEXTS.worklogs;

  const getVerificationStatusComponent = (verification) => {
    let imgWrapperName = '';
    let text = '';

    if (verification.createdAt) {
      imgWrapperName = 'createdVerificationImgWrapper';
      text = 'Email not sent';
    }
    if (verification.emailSent) {
      imgWrapperName = 'pendingVerificationImgWrapper';
      text = 'Pending Verification';
    }
    if (verification.verifiedAt) {
      imgWrapperName = 'verifiedImgWrapper';
      text = 'Verified';
    }
    if (verification.redirectedAt && verification.redirectedTo?.length) {
      imgWrapperName = 'verifiedImgWrapper';
      text = 'Verified'; // 'Redirected'
    }

    return (
      <div className={classnames(classes.eventStatusWrapper)}>
        <Typography className={classnames(classes.eventText, classes[imgWrapperName])}>
          {text}
        </Typography>
      </div>
    );
  };

  const handleVerifyModalOpen = () => {
    setVerifyModal({ open: true });
  }

  const handleVerifyModalClose = () => {
    setVerifyModal({ open: false });
  }

  const handleVerifyModalSave = () => {
    setRedirectModal({ open: true })
  }

  const handleDwlVerify = (type) => (redirectTo) => {
    if (type === 'verify') {
      dispatch(verifyWorklog(sheet._id, {}, openSnackbar));
    } else if (type === 'redirect') {
      dispatch(verifyWorklog(sheet._id, { redirectTo }, openSnackbar))
    }
    setVerifyModal({ open: false });
    setRedirectModal({ open: false });
  }

  const handleRemoveDwlUser = (userId) => () => {
    dispatch(removeDwlUser(lastSubmission._id, userId, openSnackbar));
    setDetailsShown(false);
  }

  const handleRedirectVerifyModalClose = () => {
    setRedirectModal({ open: false })
  }

  const closeDetailsView = () => {
    setDetailsShown(false)
  }
  const openDetailsView = () => {
    setDetailsShown(true)
  }


  const showVerifyButton = useMemo(() => {
    if (!lastSubmission) return false;
    if (!!historyState) return false;

    return sheet?.verificationStatus !== 'verified' && (
      lastSubmission.dwlVerification?.some((contact) => contact.userId === user._id && !contact.verifiedAt && !contact.redirectedAt) ||
      (hasPermissionsFor('dwlVerification') && lastSubmission.dwlVerification?.every((contact) => contact.userId !== user._id))
    );
  }, [user, lastSubmission, historyState])

  const hasProjectChanged = historyState?.diff?.projectId?.length;
  const hasContractorChanged = historyState?.diff?.contractorId?.length;
  const hasGridDateChanged = historyState?.diff?.grid?.date?.length;
  const { addedItems: addedRallyPoints, removedItems: removedRallyPoints } = useArrayHistoryChanges(historyState?.diff?.rallyPoints);
  const { addedItems: addedLocations, removedItems: removedLocations } = useArrayHistoryChanges(historyState?.diff?.locations);


  const renderRallyPointsLocations = (locationId, index, type) => {
    const isAdded = type === 'sheetRallyPoints'
      ? addedRallyPoints?.includes(locationId)
      : addedLocations?.includes(locationId);
    const isRemoved = type === 'sheetRallyPoints'
      ? removedRallyPoints?.includes(locationId)
      : removedLocations?.includes(locationId);
    const text = type === 'sheetRallyPoints'
      ? 'Rally Point'
      : 'Project Location';
    const arrayToCheck = type === 'sheetRallyPoints'
      ? sheet.rallyPoints || []
      : sheet.locations || []
    return (
      <Grid item xs={12} md={6} key={`sheetLocation_${locationId}_${index}`}>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classnames(worklogClasses.regularFontWeight, worklogClasses.paddingBottom)}
        >
          {text}{(arrayToCheck.length === 1 || !arrayToCheck?.includes(locationId)) ? null : ` ${index + 1}`}:
        </Typography>
        <Typography variant="body1" color="textPrimary" className={worklogClasses.locationFlex}>
          <span className={classnames(
            (historyState && isAdded) ? worklogClasses.showDiff : null,
            (historyState && isRemoved) ? worklogClasses.showDiffRemoved : null
          )}>
            {sheetLocation(sheet, true, locationId, type)}
          </span>
          <LocationPin location={findLocation(sheet, locationId, type)} />
        </Typography>
      </Grid>
    )
  }

  const getCreatedOnText = () => {
    return `Created on ${
        agoDate(sheet.createdAtDate, null, dayDateSettingsFormat)
      } at ${clockTime(
        sheet.createdAtDate,
        getTimeFormat([], timeFormat, dateFormat).formatForTimePars
      )} ${getSheetOwner()}`
  }

  return (
    <>
      <Grid item xs={12}>
        <div className={classes.sheetLocation}>
          <img src={BusinessIcon} className={classnames(classes.locationIcon)} alt="location" />
          {locations?.find(({ _id }) => _id === sheet.tenantLocationId)?.name}
        </div>

        <Typography variant="h4" className={worklogClasses.header}>
          <span className={classnames((hasProjectChanged) ? worklogClasses.showDiff : null)}>
            {getProjectName(sheet.project)}
            {sheet.project.county ? `, ${sheet.project.county}` : ''}

            {sheet.project.jobNumber && (
              <span className={worklogClasses.jobNumber}>#{sheet.project.jobNumber}</span>
            )}
          </span>
        </Typography>

        <IconButton
          className={worklogClasses.closeButton}
          onClick={close}
          aria-label="Close"
          disableRipple
        >
          <CloseIcon />
        </IconButton>

        <div className={worklogClasses.headerButtonsWrapper}>
          {(hasAccess && sheet.submissions?.length > 1) && (
            <Button
              variant="text"
              className={classnames(
                worklogClasses.primaryTextButton,
                worklogClasses.marginRightBig,
              )}
              onClick={onHistoryDrawerOpen}
              disableRipple
            >
              <Tooltip title="History">
                <img src={HistoryI} alt="history" />
              </Tooltip>
            </Button>
          )}
          {hasAccess && duplicateRule && (
            <Button
              variant="text"
              className={classnames(
                worklogClasses.primaryTextButton,
                worklogClasses.marginRightBig
              )}
              onClick={() => setDuplicateModal((p) => ({ ...p, isOpen: true }))}
              disableRipple
            >
              <Tooltip title="Duplicate">
                <img src={DuplicateI} alt="duplicate" />
              </Tooltip>
            </Button>
          )}
          <Button
            variant="text"
            className={classnames(worklogClasses.primaryTextButton)}
            disableRipple
          >
            <a
              className={classnames(worklogClasses.primaryTextButton)}
              onClick={() => {
                if (sheet?.images?.length) {
                  setPrintModal(true);
                } else {
                  openPrintPage(null); //print without img
                }
              }}
              // href={openPrintPage()}
              // target="_blank"
            >
              <Tooltip title="Print">
                <img src={PrintIcon} alt="print" />
              </Tooltip>
            </a>
          </Button>
        </div>
        <div className={classes.metaBlock} >
          <div>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(worklogClasses.regularFontWeight)}
              style={{ marginBottom: 4 }}
            >
              {getCreatedOnText()}
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(worklogClasses.regularFontWeight, worklogClasses.marginBottom)}
            >
              {getSheetStatus()}
            </Typography>
            {(isVerified || isPendingVerification)
              && (<Typography
                variant="body1"
                color="textSecondary"
                className={classnames(worklogClasses.mediumFontWeight)}
                style={{
                  color: sheetVerificationColor
                }}
              >
                {!historyState && sheet.submittedAt && (
                  <Button
                    color="secondary"
                    className={worklogClasses.expandButton}
                    onClick={detailsShown ? closeDetailsView : openDetailsView}
                    disableRipple
                  >
                    <ExpandMoreIcon />
                  </Button>
                )}
                {getSheetVerificationStatus()}
              </Typography>)
            }
          </div>
          {detailsShown && (
            <Paper
              elevation={3}
              style={{
                width: 500,
                padding: 16,
                position: 'absolute',
                overflowY: 'auto',
                top: 70,
                left: 0,
                zIndex: 6,
              }}
            >
              <Grid container spacing={1}>
                {lastSubmission?.dwlVerification?.map((verification) => (
                  <Grid key={verification.userId} container item xs={12} style={{ alignItems: 'center' }}>
                    <Grid item xs={3}>
                      <Typography style={{ fontWeight: 'bold' }}>{verification.name}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      {format(new Date(verification.redirectedAt || verification.verifiedAt || verification.emailSent || verification.createdAt), dateFormatWithTime)}
                    </Grid>
                    <Grid item xs={4}>
                      {getVerificationStatusComponent(verification)}
                    </Grid>
                    {!verification.verifiedAt && !verification.redirectedAt && hasPermissionsFor('dwlVerification') && (
                      <Grid item xs={1} className={classes.removeDwlVerificationWrapper}>
                        <Button className={classes.removeDwlVerification} onClick={handleRemoveDwlUser(verification.userId)}>
                          <CloseIcon />
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
          {showVerifyButton && (
            <div className={classes.verifyButtonHolder}>
              <Button
                variant="outlined"
                className={worklogClasses.verifyButton}
                disableRipple
                onClick={handleVerifyModalOpen}
              >
                Verify
              </Button>
            </div>
          )}
        </div>
      </Grid>

      <Grid item xs={12}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={9}>
            <div className={worklogClasses.flexBox}>
              <div className={worklogClasses.photoWrapper}>
                <img
                  src={getImageSrc(sheet.project.contractor && sheet.project.contractor.logo)}
                  className={worklogClasses.photo}
                  alt="logo"
                />
              </div>
              <Typography variant="body1" color="textPrimary" className={worklogClasses.name}>
                <span className={classnames(hasContractorChanged ? worklogClasses.showDiff : null)}>
                  {sheet.project.contractor && sheet.project.contractor.name}
                </span>
              </Typography>
              {sheet.forceAccount && sheet.forceAccount !== 'No' && (
                <div className={worklogClasses.flexWrapper}>
                  <Typography variant="body2" className={worklogClasses.forceAccount}>
                    T&M: {sheet.forceAccount}
                  </Typography>
                </div>
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={3}>
            <QualityControlActions sheet={sheet} user={user} openSnackbar={openSnackbar} />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={6} md={4}>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classnames(worklogClasses.regularFontWeight, worklogClasses.paddingBottom)}
        >
          Date:
        </Typography>
        <Typography variant="body1" color="textPrimary">
          {agoDate(sheet.createdAt, null, dayDateSettingsFormat)}
        </Typography>
      </Grid>
      <Grid item xs={6} md={4}>
        <div className={worklogClasses.projectHeader}>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classnames(worklogClasses.regularFontWeight, worklogClasses.paddingBottom)}
          >
            Grid View Date:
          </Typography>
          {hasAccess && !!sheet.projectId && (
            <Button
              variant="text"
              className={classnames(worklogClasses.primaryTextButton, worklogClasses.hideOnDesktop)}
              onClick={openAdditionalDrawer('general')}
              disableRipple
            >
              <Tooltip title="Edit">
                <img src={EditIcon} alt="edit" />
              </Tooltip>
            </Button>
          )}
        </div>
        <Typography variant="body1" color="textPrimary">
          <span className={classnames(hasGridDateChanged ? worklogClasses.showDiff : null)}>
            {formatGridDate(sheet.grid.date)}
          </span>
        </Typography>
      </Grid>
      {!!sheet.projectId && (
        <Grid item xs={6} md={4}>
          <div className={worklogClasses.projectHeader}>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(worklogClasses.regularFontWeight)}
            >
              Project ID:
            </Typography>
            {hasAccess && (
              <Button
                variant="text"
                className={classnames(worklogClasses.primaryTextButton, worklogClasses.hideOnMobile)}
                onClick={openAdditionalDrawer('general')}
                disableRipple
              >
                <Tooltip title="Edit">
                  <img src={EditIcon} alt="edit" />
                </Tooltip>
              </Button>
            )}
          </div>
          <Typography variant="body1" color="textPrimary">
            <span className={classnames(hasProjectChanged ? worklogClasses.showDiff : null)}>
              {(sheet.project && sheet.project.id) || 'N/A'}
            </span>
          </Typography>
        </Grid>
      )}

      {!!sheet.rallyPoints?.length || removedRallyPoints?.length ? (
        (sheet.rallyPoints || []).concat(removedRallyPoints || []).map((locationId, index) => (
          renderRallyPointsLocations(locationId, index, 'sheetRallyPoints')
        ))
      ) : (
        <Grid item xs={12} md={6}>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classnames(worklogClasses.regularFontWeight, worklogClasses.paddingBottom)}
          >
            Rally Point:
          </Typography>
          <Typography variant="body1" color="textPrimary" className={worklogClasses.locationFlex}>
            N/A
          </Typography>
        </Grid>
      )}

      {sheetHasLocation || removedLocations?.length ? (
        (sheet.locations || []).concat(removedLocations || []).map((locationId, index) => (
          renderRallyPointsLocations(locationId, index, 'sheetLocations')
        ))
      ) : (
        <Grid item xs={12} md={6}>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classnames(worklogClasses.regularFontWeight, worklogClasses.paddingBottom)}
          >
            Project Location:
          </Typography>
          <Typography variant="body1" color="textPrimary" className={worklogClasses.locationFlex}>
            N/A
          </Typography>
        </Grid>
      )}

      <SheetsContacts sheet={sheet} historyState={historyState} />

      <OptionalModal
        isOpen={printModal}
        onCancel={() => {
          setPrintModal(false);
        }}
        onSave={(query) => {
          openPrintPage(query);
          setPrintModal(false);
        }}
        options={options}
      />

      <VerifyModal
        isOpen={verifyModal?.open}
        onClose={handleVerifyModalClose}
        onCancel={handleDwlVerify('verify')}
        onSave={handleVerifyModalSave}
      />

      <RedirectVerifyModal
        isOpen={redirectModal?.open}
        onClose={handleRedirectVerifyModalClose}
        onCancel={handleRedirectVerifyModalClose}
        onSave={handleDwlVerify('redirect')}
        sheet={sheet}
      />
    </>
  );
};

GeneralInfoBlock.propTypes = {
  sheet: propTypes.object.isRequired,
  hasAccess: propTypes.bool.isRequired,
  close: propTypes.func.isRequired,
  openSnackbar: propTypes.func.isRequired,
  openAdditionalDrawer: propTypes.func.isRequired,
};

export default withStyles(styles)(GeneralInfoBlock);
